<template>
  <div class="tw-flex tw-gap-2">
    <CustomerActionButton
      v-for="action in actions"
      :key="action.label"
      @click="action.onClick()"
      :tooltip="action.label"
    >
      <component
        :is="action.icon"
        v-if="action.icon"
        class="tw-w-5 tw-h-5"
        :class="action.iconClass"
      />
    </CustomerActionButton>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-model="showConfirmationModal"
      :title="t('suppliers.titles.attention').toUpperCase()"
      :message="confirmationMessage"
      type="danger"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="confirmAction"
      @cancel="closeModal"
    />
  </div>
</template>

<script setup lang="ts">
import type { SupplierInList } from '@/_helpers/interfaces/suppliers/supplier-in-list.interface';
import { useI18n } from 'vue-i18n';
import { ref, shallowRef } from 'vue';
import { useSupplierStore } from '@/stores/supplier.store';
import { useRouter } from 'vue-router';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import EditIcon from '@/assets/icons/Edit.svg';
import TrashIcon from '@/assets/icons/Trash.svg';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';

const { t } = useI18n();
const router = useRouter();
const { handleApiError } = useApiUtils();

const props = defineProps<{
  supplier: SupplierInList;
}>();

const emit = defineEmits<{
  reloadTable: [];
}>();

const { deleteSupplier } = useSupplierStore();

// Modal state
const showConfirmationModal = ref(false);
const confirmationMessage = ref('');

const handleEdit = () => {
  router.push({
    name: 'edit-supplier',
    params: { supplierId: props.supplier.id },
  });
};

const handleDelete = () => {
  confirmationMessage.value = t('suppliers.titles.confirm_supplier_delete', {
    suppliersCount: 'this',
  });
  showConfirmationModal.value = true;
};

const closeModal = () => {
  showConfirmationModal.value = false;
  confirmationMessage.value = '';
};

const confirmAction = async () => {
  await handleApiError(async () => {
    await deleteSupplier(props.supplier.id);
    emit('reloadTable');
  }, t('suppliers.titles.supplier_deleted_successfully'));

  closeModal();
};

const actions = shallowRef<any[]>([
  {
    label: t('suppliers.titles.edit'),
    icon: EditIcon,
    iconClass: 'tw-text-primary-600',
    onClick: handleEdit,
  },
  {
    label: t('common.forms.delete'),
    icon: TrashIcon,
    iconClass: 'tw-text-red-500',
    onClick: handleDelete,
  },
]);
</script>
